import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { cancelReceiptVoucher, deleteReceiptVoucher, getReceiptVouchers } from '../../services/ReceiptVoucherServices';
import { displayError } from '../../helpers';
import { fetchAndShowReceiptVoucherPdf } from '../../utils/receiptVoucherPdf';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolveCustomerFilter,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';

function ReceiptVoucherTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const customerIds = resolveCustomerFilter(salesSettings, customers);
        const [rangeFrom, rangeTo] = activeDateRange;

        if (!plantId || !customerIds || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        return getReceiptVouchers(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
            customer_ids: customerIds,
        })
            .then((response) => {
                setData({
                    records: (response.receipt_voucher_list || []).map((row) => ({
                        ...row,
                        status_label: row.status ? 'Active' : 'Cancelled',
                        hide_row_edit: row.status ? 'No' : 'Yes',
                        hide_row_cancel: row.status ? 'No' : 'Yes',
                        hide_row_delete: row.status ? 'Yes' : 'No',
                        hide_row_print: 'No',
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load receipt vouchers.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, customers, plants, salesSettings]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'receipt_no', headerName: 'Receipt No' },
        { fieldName: 'receipt_date', headerName: 'Receipt Date' },
        { fieldName: 'customer_name', headerName: 'Customer' },
        { fieldName: 'bank_name', headerName: 'Bank' },
        { fieldName: 'receipt_mode', headerName: 'Mode' },
        { fieldName: 'receipt_against', headerName: 'Against' },
        { fieldName: 'receipt_amount', headerName: 'Amount' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return data.records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return data.records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [data.records, searchText]);

    const summaryStats = useMemo(() => {
        const activeCount = filteredRecords.filter((row) => row.status).length;
        const totalAmount = filteredRecords.reduce((sum, row) => sum + (parseFloat(row.receipt_amount) || 0), 0);
        return [
            { label: 'ACTIVE', value: String(activeCount), accent: 'b2b' },
            { label: 'TOTAL AMOUNT', value: totalAmount.toFixed(2), accent: 'b2c' },
        ];
    }, [filteredRecords]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, customers),
        [salesSettings, plants, customers],
    );

    const onEdit = (recordId) => {
        const row = data.records.find((item) => item.id === recordId);
        if (row && !row.status) {
            navigate(`/ReceiptVoucherEdit/${recordId}`);
            return;
        }
        navigate(`/ReceiptVoucher/edit/${recordId}`);
    };

    const onCancel = (recordId, event) => {
        event?.preventDefault();
        Swal.fire({
            title: 'Cancel receipt voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setIsLoading(true);
            cancelReceiptVoucher(cookies, recordId)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'Receipt voucher cancelled.', 'success');
                    return fetchRecords();
                })
                .catch((error) => {
                    displayError(error?.response?.data || error, 'Cancel Failed');
                })
                .finally(() => setIsLoading(false));
        });
    };

    const onDelete = (recordId, event) => {
        event?.preventDefault();
        Swal.fire({
            title: 'Delete cancelled receipt voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setIsLoading(true);
            deleteReceiptVoucher(cookies, recordId)
                .then((response) => {
                    Swal.fire('Deleted', response.message || 'Receipt voucher deleted.', 'success');
                    return fetchRecords();
                })
                .catch((error) => {
                    displayError(error?.response?.data || error, 'Delete Failed');
                })
                .finally(() => setIsLoading(false));
        });
    };

    const onPrint = (recordId, event) => {
        event?.preventDefault();
        setIsLoading(true);
        fetchAndShowReceiptVoucherPdf(cookies, recordId, { title: 'Receipt Voucher' })
            .finally(() => setIsLoading(false));
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            filteredRecords,
            'ReceiptVoucher',
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Receipt Voucher',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            filteredRecords,
            'ReceiptVoucher.pdf',
        );
    };

    const handleSettingsSave = (nextSettings) => {
        saveSalesListSettings(cookies.myUserId, nextSettings);
        setSalesSettings(nextSettings);
        setShowSettings(false);
        Swal.fire('Updated!', '', 'success');
    };

    useEffect(() => {
        Promise.all([getUserAllowedPlants(cookies), getAllCustomerMain(cookies)])
            .then(([plantList, customerList]) => {
                setPlants((plantList.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
                setCustomers((customerList.customer_list || [])
                    .filter((customer) => customer.status)
                    .map((customer) => ({ value: customer.id, label: customer.name })));
                setIsInitialized(true);
            })
            .catch(() => setIsLoading(false));
    }, [cookies]);

    useEffect(() => {
        if (!isInitialized || plants.length === 0) {
            return;
        }
        fetchRecords();
    }, [isInitialized, plants.length, customers.length, salesSettings, activeDateRange, fetchRecords]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="List of Receipt Voucher"
                    subtitle="Customer receipts — bank/cash debit, sundry debtors credit."
                    modeLabel="VIEW MODE"
                    recordCount={filteredRecords.length}
                    recordLabel="receipt vouchers"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    formLink="/ReceiptVoucher"
                    addNewLabel="Add New"
                >
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="List of Receipt Voucher"
                                columns={columns}
                                data={filteredRecords}
                                onRowEdit={onEdit}
                                onRowCancel={onCancel}
                                onRowDelete={onDelete}
                                onRowPrint={onPrint}
                                hidePrint={false}
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                autoPinActionColumn={false}
                                compactRows
                            />
                        </div>
                    </div>
                </ListPageLayout>

                <SalesListSettingsModal
                    show={showSettings}
                    allowedPlants={plants}
                    customers={customers}
                    initialSettings={salesSettings}
                    onSave={handleSettingsSave}
                    onClose={() => setShowSettings(false)}
                />
            </div>
        </div>
    );
}

export default ReceiptVoucherTable;
